import { createFileRoute, useRouteContext, useNavigate } from '@tanstack/react-router'
import React, { useState, useEffect, Fragment, useRef, useMemo } from 'react'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  useReactTable,
  ColumnDef,
  SortingState,
} from '@tanstack/react-table'
import Tooltip from '../components/Tooltip'
import MarketDepthChart from '../components/MarketDepthChart'
import RelatedOutcomes from '../components/RelatedOutcomes'
import { generateKalshiUrl } from '../../../../packages/shared/utils'
import { PlatformLogo } from '../components/PlatformLogos'
import MarketDrawer from '../components/MarketDrawer'

export const Route = createFileRoute('/_layout/markets')({
  component: MarketsComponent,
})

type Platform = 'polymarket' | 'kalshi' | 'predictit' | 'limitless'

type Market = {
  id: string
  platform: string
  question: string
  category?: string
  market_type?: string
  yes_price?: number
  no_price?: number
  volume?: number
  liquidity?: number
  end_date?: string | number
  ticker?: string
  slug?: string
  condition_id?: string
  parent_event_id?: string
  event_title?: string
  outcomes?: any[]
  subRows?: Market[]
  is_series?: boolean
  series_ticker?: string
  child_count?: number
}

function MarketsComponent() {
  const navigate = useNavigate()
  const context = useRouteContext({ from: '/_layout/markets' })
  
  // Initialize with context data or defaults
  const [globalStats, setGlobalStats] = useState(context?.globalStats || {
    totalMarkets: 0,
    totalVolume: 0,
    totalLiquidity: 0,
    avgYesPrice: 0,
    avgNoPrice: 0,
    avgSpread: 0,
    opportunityCount: 0,
    platforms: {
      polymarket: { count: 0, volume: 0, liquidity: 0 },
      kalshi: { count: 0, volume: 0, liquidity: 0 },
      predictit: { count: 0, volume: 0, liquidity: 0 },
      limitless: { count: 0, volume: 0, liquidity: 0 }
    }
  })
  
  // Fetch stats on client side if not available from SSR
  useEffect(() => {
    if (globalStats.totalMarkets === 0) {
      fetch('https://skybit-api.botnet-599.workers.dev/api/stats')
        .then(res => res.json())
        .then(data => {
          if (data) {
            setGlobalStats({
              totalMarkets: data.totalMarkets || 0,
              totalVolume: data.totalVolume || 0,
              totalLiquidity: data.totalLiquidity || 0,
              avgYesPrice: data.avgYesPrice || 0,
              avgNoPrice: data.avgNoPrice || 0,
              avgSpread: data.avgSpread || 0,
              opportunityCount: data.unique_opportunities || 0,
              platforms: data.platforms || {
                polymarket: { count: 0, volume: 0, liquidity: 0 },
                kalshi: { count: 0, volume: 0, liquidity: 0 },
                predictit: { count: 0, volume: 0, liquidity: 0 },
                limitless: { count: 0, volume: 0, liquidity: 0 }
              }
            })
          }
        })
        .catch(err => console.error('Failed to fetch stats:', err))
    }
  }, [])
  
  // Platform filter state - array of selected platforms
  
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>(() => {
    const params = new URLSearchParams(window.location.search)
    const platforms = params.get('platforms')
    if (platforms) {
      return platforms.split(',')
    }
    return ['polymarket', 'kalshi', 'predictit', 'limitless']
  })
  
  const [selectedMarketTypes, setSelectedMarketTypes] = useState<string[]>(() => {
    const params = new URLSearchParams(window.location.search)
    const types = params.get('market_types')
    if (types) {
      return types.split(',')
    }
    return ['binary', 'group']
  })
  const [showEndedMarkets, setShowEndedMarkets] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return params.get('show_ended') === 'true'
  })
  const [searchQuery, setSearchQuery] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return params.get('search') || ''
  })
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery)
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [sorting, setSorting] = useState<SortingState>(() => {
    const params = new URLSearchParams(window.location.search)
    const sortBy = params.get('sortBy') || 'volume'
    const sortDir = params.get('sortDir') || 'desc'
    return [{ id: sortBy, desc: sortDir === 'desc' }]
  })
  const [volumeFilter, setVolumeFilter] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return {
      min: Number(params.get('volumeMin')) || 0,
      max: params.get('volumeMax') ? Number(params.get('volumeMax')) : null
    }
  })
  const [liquidityFilter, setLiquidityFilter] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return {
      min: Number(params.get('liquidityMin')) || 0,
      max: params.get('liquidityMax') ? Number(params.get('liquidityMax')) : null
    }
  })
  const [spreadFilter, setSpreadFilter] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return {
      min: Number(params.get('spreadMin')) || 0,
      max: Number(params.get('spreadMax')) || 100
    }
  })
  
  // Dropdown states
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  
  // Drawer state
  const [selectedMarket, setSelectedMarket] = useState<Market | null>(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  
  // Check if any filters are active (not default)
  const hasActiveFilters = () => {
    return (
      selectedPlatforms.length < 4 ||
      selectedMarketTypes.length < 2 ||
      showEndedMarkets ||
      debouncedSearchQuery !== '' ||
      volumeFilter.min > 0 ||
      volumeFilter.max !== null ||
      liquidityFilter.min > 0 ||
      liquidityFilter.max !== null ||
      spreadFilter.min > 0 ||
      spreadFilter.max < 100
    )
  }
  
  // Reset all filters to default
  const resetAllFilters = () => {
    setSelectedPlatforms(['polymarket', 'kalshi', 'predictit', 'limitless'])
    setSelectedMarketTypes(['binary', 'group'])
    setShowEndedMarkets(false)
    setSearchQuery('')
    setDebouncedSearchQuery('')
    setVolumeFilter({ min: 0, max: null })
    setLiquidityFilter({ min: 0, max: null })
    setSpreadFilter({ min: 0, max: 100 })
    setMarketsPage(1)
  }
  
  const [markets, setMarkets] = useState<Market[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [marketsPage, setMarketsPage] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return parseInt(params.get('page') || '1')
  })
  const [hasMoreMarkets, setHasMoreMarkets] = useState(true)
  const [totalMarkets, setTotalMarkets] = useState(0)
  
  // Platform stats are now fetched from globalStats in the route context
  
  const [enhancedMarketData, setEnhancedMarketData] = useState<Record<string, any>>({})
  const [loadingEnhancedData, setLoadingEnhancedData] = useState(new Set<string>())
  const [marketsCache, setMarketsCache] = useState<Record<string, Market[]>>({})
  const [loadingAll, setLoadingAll] = useState(false)
  const [archivedMarkets, setArchivedMarkets] = useState<Market[]>([])
  const [loadingArchived, setLoadingArchived] = useState(false)

  // Debouncing effect for search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
      setMarketsPage(1)
    }, 300)
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchQuery])

  // Click outside handler for dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setActiveDropdown(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams()
    
    // Platform filter
    if (selectedPlatforms.length > 0 && selectedPlatforms.length < 3) {
      params.set('platforms', selectedPlatforms.join(','))
    }
    
    // Search query
    if (debouncedSearchQuery) {
      params.set('search', debouncedSearchQuery)
    }
    
    // Volume filter
    if (volumeFilter.min > 0) {
      params.set('volumeMin', volumeFilter.min.toString())
    }
    if (volumeFilter.max !== null) {
      params.set('volumeMax', volumeFilter.max.toString())
    }
    
    // Liquidity filter
    if (liquidityFilter.min > 0) {
      params.set('liquidityMin', liquidityFilter.min.toString())
    }
    if (liquidityFilter.max !== null) {
      params.set('liquidityMax', liquidityFilter.max.toString())
    }
    
    // Spread filter
    if (spreadFilter.min > 0) {
      params.set('spreadMin', spreadFilter.min.toString())
    }
    if (spreadFilter.max < 100) {
      params.set('spreadMax', spreadFilter.max.toString())
    }
    
    // Market types
    if (selectedMarketTypes.length > 0 && selectedMarketTypes.length < 4) {
      params.set('market_types', selectedMarketTypes.join(','))
    }
    
    // Show ended markets
    if (showEndedMarkets) {
      params.set('show_ended', 'true')
    }
    
    // Sorting
    if (sorting.length > 0) {
      params.set('sortBy', sorting[0].id)
      params.set('sortDir', sorting[0].desc ? 'desc' : 'asc')
    }
    
    // Page
    params.set('page', marketsPage.toString())
    
    const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`
    window.history.replaceState({}, '', newUrl)
  }, [selectedPlatforms, selectedMarketTypes, debouncedSearchQuery, volumeFilter, liquidityFilter, spreadFilter, sorting, marketsPage, showEndedMarkets])

  // Load markets
  useEffect(() => {
    loadMarkets()
  }, [selectedPlatforms, selectedMarketTypes, marketsPage, debouncedSearchQuery, volumeFilter, liquidityFilter, spreadFilter, showEndedMarkets])

  // Handle market parameter from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const marketParam = params.get('m')
    
    if (marketParam && markets.length > 0) {
      // Parse platform:identifier format
      const [platform, ...identifierParts] = marketParam.split(':')
      const identifier = identifierParts.join(':') // In case identifier contains colons
      
      if (platform && identifier) {
        // Find the market with matching platform and identifier
        const foundMarket = markets.find(m => {
          if (m.platform !== platform) return false
          
          // Check for group markets first
          if (m.market_type === 'group' || m.parent_event_id === null) {
            return m.id === identifier || m.parent_event_id === identifier
          }
          
          // Regular markets
          if (platform === 'polymarket') {
            return m.slug === identifier || m.condition_id === identifier || m.id === identifier
          } else if (platform === 'kalshi') {
            return m.ticker === identifier || m.id === identifier
          } else {
            return m.slug === identifier || m.id === identifier
          }
        })
        
        if (foundMarket && !selectedMarket) {
          handleMarketClick(foundMarket)
        }
      }
    }
  }, [markets]) // Run when markets are loaded

  const loadMarkets = async () => {
    const cacheKey = `${selectedPlatforms.join(',')}-${selectedMarketTypes.join(',')}-${marketsPage}-${debouncedSearchQuery}-${volumeFilter.min}-${volumeFilter.max}-${liquidityFilter.min}-${liquidityFilter.max}-${spreadFilter.min}-${spreadFilter.max}-${showEndedMarkets}`
    if (marketsCache[cacheKey]) {
      setMarkets(marketsCache[cacheKey])
      return
    }
    
    setLoading(true)
    setError(null)
    
    try {
      // Build URL with all filter parameters
      const params = new URLSearchParams()
      params.set('page', marketsPage.toString())
      params.set('limit', '1000')
      
      // Platform filter
      if (selectedPlatforms.length > 0) {
        params.set('platforms', selectedPlatforms.join(','))
      }
      
      // Search query
      if (debouncedSearchQuery) {
        params.set('search', debouncedSearchQuery)
      }
      
      // Volume filter - not applicable to archived markets
      if (!showEndedMarkets) {
        if (volumeFilter.min > 0) {
          params.set('volume_min', volumeFilter.min.toString())
        }
        if (volumeFilter.max !== null) {
          params.set('volume_max', volumeFilter.max.toString())
        }
        
        // Liquidity filter
        if (liquidityFilter.min > 0) {
          params.set('liquidity_min', liquidityFilter.min.toString())
        }
        if (liquidityFilter.max !== null) {
          params.set('liquidity_max', liquidityFilter.max.toString())
        }
        
        // Spread filter
        if (spreadFilter.min > 0) {
          params.set('spread_min', spreadFilter.min.toString())
        }
        if (spreadFilter.max < 100) {
          params.set('spread_max', spreadFilter.max.toString())
        }
      }
      
      // Market types filter
      if (selectedMarketTypes.length > 0 && selectedMarketTypes.length < 2) {
        params.set('market_types', selectedMarketTypes.join(','))
      }
      
      // Choose endpoint based on showEndedMarkets
      const endpoint = showEndedMarkets ? 'api/markets/archived' : 'markets'
      const url = `https://skybit-api.botnet-599.workers.dev/${endpoint}?${params.toString()}`
      
      // If no platforms selected, return empty
      if (selectedPlatforms.length === 0) {
        setMarkets([])
        setHasMoreMarkets(false)
        setLoading(false)
        return
      }
      
      const response = await fetch(url)
      if (!response.ok) {
        const errorText = await response.text()
        console.error('API Error:', response.status, errorText)
        throw new Error(`Failed to fetch markets: ${response.status} ${response.statusText}`)
      }
      
      const data = await response.json()
      
      if (marketsPage === 1) {
        setMarkets(data.markets || [])
      } else {
        setMarkets(prev => [...prev, ...(data.markets || [])])
      }
      
      setTotalMarkets(data.total || 0)
      setHasMoreMarkets(data.hasMore || false)
      
      setMarketsCache(prev => ({
        ...prev,
        [cacheKey]: data.markets || []
      }))
    } catch (err) {
      console.error('Error loading markets:', err)
      setError(err instanceof Error ? err.message : 'Failed to load markets')
    } finally {
      setLoading(false)
    }
  }

  // Stats are now fetched from the API via globalStats in the route context

  const formatPrice = (price?: number, isGroupMarket?: boolean) => {
    if (price === undefined || price === null) return '-'
    // Special handling for group markets with 0 prices
    if (price === 0 && isGroupMarket) return '-'
    // Show 2 decimal places for precision
    const percentage = (price * 100).toFixed(2)
    return `${percentage}%`
  }

  const formatNumber = (num: number) => {
    if (num === 0) return '$0'
    if (num >= 1000000000) return `$${(num / 1000000000).toFixed(1)}B`
    if (num >= 1000000) return `$${(num / 1000000).toFixed(1)}M`
    if (num >= 10000) return `$${(num / 1000).toFixed(0)}K`
    if (num >= 1000) return `$${(num / 1000).toFixed(1)}K`
    return `$${num.toFixed(0)}`
  }

  const formatFilterNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(0)}M`
    if (num >= 1000) return `${(num / 1000).toFixed(0)}k`
    return num.toString()
  }
  
  const handleMarketClick = (market: Market) => {
    console.log('Market clicked:', market)
    setSelectedMarket(market)
    setIsDrawerOpen(true)
    
    // Update URL with market identifier
    let marketIdentifier: string
    
    // For group markets or parent events, use the parent_event_id or id
    if (market.market_type === 'group' || market.parent_event_id === null) {
      marketIdentifier = market.parent_event_id || market.id
    } else {
      // For regular markets
      marketIdentifier = market.platform === 'polymarket' 
        ? (market.slug || market.condition_id || market.id)
        : market.platform === 'kalshi'
        ? (market.ticker || market.id)
        : (market.slug || market.id)
    }
    
    if (marketIdentifier) {
      const params = new URLSearchParams(window.location.search)
      params.set('m', `${market.platform}:${marketIdentifier}`)
      const newUrl = `${window.location.pathname}?${params.toString()}`
      window.history.pushState({}, '', newUrl)
    }
    
    // Fetch enhanced data if available
    if (market.platform === 'polymarket' || market.platform === 'kalshi') {
      fetchEnhancedData(market)
    }
  }
  
  // Fetch enhanced market data (orderbook and price history)
  const fetchEnhancedData = async (market: Market) => {
    const marketKey = `${market.platform}_${market.id}`
    
    // Skip if already loading or loaded
    if (loadingEnhancedData.has(marketKey) || enhancedMarketData[marketKey]) {
      return
    }
    
    setLoadingEnhancedData(prev => new Set(prev).add(marketKey))
    
    try {
      const response = await fetch('https://skybit-api.botnet-599.workers.dev/markets/enhanced-data', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          markets: [{ 
            platform: market.platform, 
            id: market.condition_id || market.ticker || market.id,
            ticker: market.ticker  // Pass ticker explicitly for Kalshi
          }],
          includeOrderBook: true,
          includePriceHistory: true,
          historyInterval: '1h'
        })
      })
      
      if (response.ok) {
        const data = await response.json()
        if (data.results && data.results.length > 0) {
          setEnhancedMarketData(prev => ({
            ...prev,
            [marketKey]: data.results[0]
          }))
        }
      }
    } catch (error) {
      console.error('Failed to fetch enhanced data:', error)
    } finally {
      setLoadingEnhancedData(prev => {
        const newSet = new Set(prev)
        newSet.delete(marketKey)
        return newSet
      })
    }
  }
  

  const getFilterLabel = (filterType: string) => {
    switch (filterType) {
      case 'types':
        if (selectedMarketTypes.length === 0) return 'No Types'
        if (selectedMarketTypes.length === 2) return 'All Types'
        return `${selectedMarketTypes.length} Type`
      case 'volume':
        if (volumeFilter.min === 0 && volumeFilter.max === null) return 'Volume'
        if (volumeFilter.max === null) return `Volume $${formatFilterNumber(volumeFilter.min)}+`
        return `Volume $${formatFilterNumber(volumeFilter.min)}-$${formatFilterNumber(volumeFilter.max)}`
      case 'liquidity':
        if (liquidityFilter.min === 0 && liquidityFilter.max === null) return 'Liquidity'
        if (liquidityFilter.max === null) return `Liquidity $${formatFilterNumber(liquidityFilter.min)}+`
        return `Liquidity $${formatFilterNumber(liquidityFilter.min)}-$${formatFilterNumber(liquidityFilter.max)}`
      case 'spread':
        if (spreadFilter.min === 0 && spreadFilter.max === 100) return 'Spread'
        return `Spread ${spreadFilter.min}%-${spreadFilter.max}%`
      default:
        return filterType
    }
  }

  const formatTimestamp = (timestamp?: string | number) => {
    if (!timestamp) return '-'
    
    let date: Date
    if (typeof timestamp === 'string') {
      date = new Date(timestamp)
    } else {
      date = new Date(timestamp * 1000)
    }
    
    const now = new Date()
    const diff = date.getTime() - now.getTime()
    
    // Market has ended
    if (diff < 0) {
      const absDiff = Math.abs(diff)
      const days = Math.floor(absDiff / (1000 * 60 * 60 * 24))
      const hours = Math.floor((absDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      
      if (days > 30) return 'Ended'
      if (days > 0) return `Ended ${days}d ago`
      if (hours > 0) return `Ended ${hours}h ago`
      return 'Just ended'
    }
    
    // Market is still active
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    
    if (days > 0) return `${days}d ${hours}h`
    if (hours > 0) return `${hours}h`
    return 'Soon'
  }


  // Fetch child markets for a parent
  const fetchChildMarkets = async (parentId: string, row: any) => {
    try {
      const response = await fetch(`https://skybit-api.botnet-599.workers.dev/markets/parent/${parentId}`)
      const data = await response.json()
      if (data.markets && data.markets.length > 0) {
        // Add child markets as subRows
        row.original.subRows = data.markets
        // Force re-render
        setMarkets([...markets])
      }
    } catch (error) {
      console.error('Failed to fetch child markets:', error)
    }
  }
  
  // For now, just return ungrouped markets since nested events feature is not ready
  const groupMarketsByEvent = (marketsData: Market[]): Market[] => {
    return marketsData
  }

  const groupedMarkets = useMemo(
    () => {
      // Group markets by parent_event_id for all platforms that support grouping
      const marketGroups = new Map<string, Market[]>()
      const parentMarkets: Market[] = []
      const standaloneMarkets: Market[] = []
      
      // First pass: separate parent markets and group child markets
      markets.forEach(market => {
        // If this is a parent market, add it to parent list
        if (market.market_type === 'group') {
          parentMarkets.push(market)
        } 
        // If this has a parent_event_id, it's a child market
        else if (market.parent_event_id) {
          if (!marketGroups.has(market.parent_event_id)) {
            marketGroups.set(market.parent_event_id, [])
          }
          marketGroups.get(market.parent_event_id)!.push(market)
        } 
        // Otherwise it's a standalone market
        else {
          standaloneMarkets.push(market)
        }
      })
      
      // For groups without a parent market in the dataset, create synthetic parents
      // This handles cases where the API might not return parent markets
      const syntheticParents: Market[] = []
      marketGroups.forEach((groupMarkets, parentId) => {
        // Check if we already have a parent market for this group
        const hasParent = parentMarkets.some(p => p.id === parentId)
        
        if (!hasParent && groupMarkets.length > 1) {
          // Extract platform from parent ID format: {platform}_group_{id}
          const platform = parentId.split('_')[0] as Platform
          
          // Use event title from child markets or fallback to generic name
          let eventName = groupMarkets[0].event_title || groupMarkets[0].category || 'Multi-outcome Market'
          
          // Create synthetic parent
          const parent: Market = {
            ...groupMarkets[0],
            id: parentId,
            platform: platform,
            question: eventName,
            market_type: 'group',
            parent_event_id: null,
            volume: groupMarkets.reduce((sum, m) => sum + (m.volume || 0), 0),
            liquidity: groupMarkets.reduce((sum, m) => sum + (m.liquidity || 0), 0),
            // Store child count for display
            child_count: groupMarkets.length
          }
          syntheticParents.push(parent)
        } else if (!hasParent && groupMarkets.length === 1) {
          // Single market in a group without parent, treat as standalone
          standaloneMarkets.push(...groupMarkets)
        }
      })
      
      // Filter out child markets that have parents (real or synthetic)
      const allParentIds = new Set([
        ...parentMarkets.map(p => p.id),
        ...syntheticParents.map(p => p.id)
      ])
      
      const filteredMarkets = standaloneMarkets.filter(market => {
        // If it has a parent_event_id and that parent exists, filter it out
        if (market.parent_event_id && allParentIds.has(market.parent_event_id)) {
          return false
        }
        return true
      })
      
      // Return all parent markets (real and synthetic) plus standalone markets
      return [...parentMarkets, ...syntheticParents, ...filteredMarkets]
    },
    [markets]
  )

  // Column definitions
  const columnHelper = createColumnHelper<Market>()
  
  const columns = useMemo<ColumnDef<Market, any>[]>(
    () => [
      columnHelper.accessor('question', {
        header: 'QUESTION',
        cell: ({ row, getValue }) => {
          const market = row.original
          return (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              overflow: 'hidden',
              padding: '0 0.5rem',
              gap: '0.5rem'
            }}>
              {market.is_series && (
                <Tooltip content="This is a recurring periodic market with similar structure (e.g., 'Will unemployment be above 4% for [Month] 2024?' repeated monthly)">
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '16px',
                    height: '16px',
                    borderRadius: '2px',
                    backgroundColor: 'rgba(var(--sacred-accent-rgb), 0.1)',
                    color: 'var(--sacred-accent)',
                    fontSize: '12px',
                    flexShrink: 0,
                    cursor: 'help'
                  }}>
                    ↻
                  </span>
                </Tooltip>
              )}
              <span style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1
              }}>{getValue()}</span>
            </div>
          )
        },
      }),
      columnHelper.accessor('platform', {
        header: () => (
          <Tooltip content="Trading platform hosting this market">
            <span style={{ textDecoration: 'underline', textDecorationStyle: 'dotted' }}>MARKET</span>
          </Tooltip>
        ),
        cell: info => (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', height: '100%' }}>
            <PlatformLogo platform={info.getValue()} size={18} showText={false} />
          </div>
        ),
      }),
      columnHelper.accessor('market_type', {
        header: () => (
          <Tooltip content={
            <div style={{ fontSize: '0.8rem', lineHeight: '1.4' }}>
              <div style={{ marginBottom: '0.5rem', fontWeight: 'bold' }}>Market Types:</div>
              <ul style={{ margin: '0 0 0.5rem 1rem', padding: 0 }}>
                <li><strong>Binary</strong> - YES/NO markets (will this happen or not?)</li>
                <li><strong>Group</strong> - Multiple outcome markets (which one will win?)</li>
              </ul>
            </div>
          }>
            <span style={{ textDecoration: 'underline', textDecorationStyle: 'dotted' }}>TYPE</span>
          </Tooltip>
        ),
        cell: info => {
          const market = info.row.original
          const isGroup = market.market_type === 'group'
          
          // Determine the simple type to display
          let icon = '○'
          let text = 'MARKET'
          let typeDescription = 'Prediction market'
          
          if (isGroup) {
            icon = '⊞' // Grid/Plus box for group
            text = 'GROUP'
            typeDescription = 'A collection of related prediction markets'
            // Add child count for Polymarket groups
            if (market.platform === 'polymarket' && market.child_count) {
              text = `GROUP (${market.child_count})`
            }
          } else if (market.market_type === 'categorical' || market.market_type === 'multiple_choice' || market.contract_type === 'multiple_choice' || (market.outcomes && market.outcomes.length > 2)) {
            icon = '⊞' // Grid/Plus box for group
            text = 'GROUP'
            typeDescription = 'Multiple choice - one outcome wins from many options'
          } else if (market.yes_price !== undefined && market.no_price !== undefined) {
            icon = '◐' // Half circle for binary
            text = 'BINARY'
            typeDescription = 'Binary market - will this event happen or not?'
          } else {
            // Fallback logic based on platform-specific patterns
            if (market.platform === 'polymarket' && market.outcomes && market.outcomes.length > 2) {
              icon = '≡'
              text = 'MULTI'
              typeDescription = 'Multiple choice market'
            } else {
              icon = '◐'
              text = 'BINARY'
              typeDescription = 'Binary market'
            }
          }

          return (
            <Tooltip content={typeDescription}>
              <span className="market-type-cell" style={{ 
                cursor: 'help',
                fontSize: '0.6rem',
                fontWeight: 500,
                letterSpacing: '0.05em',
                color: 'var(--sacred-text-dim)'
              }}>
                {text}
              </span>
            </Tooltip>
          )
        },
        sortingFn: 'basic',
        size: 80, // Adjusted for icon + text
      }),
      columnHelper.accessor('yes_price', {
        header: () => (
          <div title="Current price for YES shares (probability of event occurring)">
            <Tooltip content="Current price for YES shares (probability of event occurring)">
              <span style={{ textDecoration: 'underline', textDecorationStyle: 'dotted' }}>YES</span>
            </Tooltip>
          </div>
        ),
        cell: info => {
          const market = info.row.original
          const isGroupMarket = market.market_type === 'group' || 
                               market.parent_event_id !== null
          return (
            <span className="price-yes">{formatPrice(info.getValue(), isGroupMarket)}</span>
          )
        },
        sortingFn: 'basic',
      }),
      columnHelper.accessor('no_price', {
        header: () => (
          <Tooltip content="Current price for NO shares (probability of event NOT occurring)">
            <span style={{ textDecoration: 'underline', textDecorationStyle: 'dotted' }}>NO</span>
          </Tooltip>
        ),
        cell: info => {
          const market = info.row.original
          const isGroupMarket = market.market_type === 'group' || 
                               market.parent_event_id !== null
          return (
            <span className="price-no">{formatPrice(info.getValue(), isGroupMarket)}</span>
          )
        },
        sortingFn: 'basic',
      }),
      columnHelper.accessor(row => {
        // Group markets don't have yes/no prices, so spread doesn't apply
        if (row.market_type === 'group') {
          return null
        }
        // For binary markets, calculate spread
        if (row.yes_price !== undefined && row.no_price !== undefined) {
          return Math.abs((row.yes_price + row.no_price) - 1)
        }
        return null
      }, {
        id: 'spread',
        header: () => (
          <Tooltip content="Price inefficiency between YES and NO prices">
            <span style={{ textDecoration: 'underline', textDecorationStyle: 'dotted' }}>SPREAD</span>
          </Tooltip>
        ),
        cell: info => {
          const spread = info.getValue()
          if (spread === null) {
            return <span style={{ color: 'var(--sacred-text-dim)' }}>-</span>
          }
          return (
            <span className={spread > 0.05 ? 'text-warning' : ''}>
              {(spread * 100).toFixed(2)}%
            </span>
          )
        },
        sortingFn: 'basic',
      }),
      columnHelper.accessor('volume', {
        header: () => (
          <Tooltip content="Total dollar volume traded on this market">
            <span style={{ textDecoration: 'underline', textDecorationStyle: 'dotted' }}>VOLUME</span>
          </Tooltip>
        ),
        cell: info => formatNumber(info.getValue() || 0),
        sortingFn: 'basic',
        size: 80, // Smaller column width
      }),
      columnHelper.accessor('liquidity', {
        header: () => (
          <Tooltip content="Available liquidity for trading">
            <span style={{ textDecoration: 'underline', textDecorationStyle: 'dotted' }}>LIQUIDITY</span>
          </Tooltip>
        ),
        cell: info => formatNumber(info.getValue() || 0),
        sortingFn: 'basic',
        size: 80, // Smaller column width
      }),
      columnHelper.accessor('end_date', {
        header: () => (
          <Tooltip content="Time remaining until market closes">
            <span style={{ textDecoration: 'underline', textDecorationStyle: 'dotted' }}>EXPIRES</span>
          </Tooltip>
        ),
        cell: info => {
          const value = formatTimestamp(info.getValue())
          const isEnded = value.includes('Ended') || value === 'Just ended'
          
          return (
            <span style={{ 
              fontSize: '0.6rem',
              fontWeight: 500,
              letterSpacing: '0.05em',
              color: isEnded ? 'var(--sacred-red)' : 'var(--sacred-text-dim)',
              opacity: isEnded ? 0.8 : 1
            }}>
              {value}
            </span>
          )
        },
        sortingFn: 'basic',
      }),
    ],
    []
  )

  const table = useReactTable({
    data: groupedMarkets,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })


  return (
    <div className="markets-page">
      {/* Stats Bar */}
      <div className="stats-bar">
        <div className="stat-group">
          <div className="stat-header">
            <span className="stat-title">Markets</span>
            <span className="stat-subtitle">{showEndedMarkets ? 'Archived (Last 30 Days)' : 'Current Active'}</span>
          </div>
          <div className="stat-main-value">
            {showEndedMarkets 
              ? (totalMarkets || 0).toLocaleString() 
              : globalStats.totalMarkets.toLocaleString()
            }
          </div>
          <div className="stat-breakdown">
            {globalStats.platforms && Object.entries(globalStats.platforms)
              .filter(([_, stats]) => stats.count > 0)
              .sort((a, b) => b[1].count - a[1].count)
              .map(([platform, stats]) => (
                <div key={platform} className="stat-platform">
                  <span className="platform-name">{platform.charAt(0).toUpperCase() + platform.slice(1)}</span>
                  <span className="platform-value">{stats.count.toLocaleString()}</span>
                </div>
              ))
            }
          </div>
        </div>
        
        <div className="stat-group">
          <div className="stat-header">
            <span className="stat-title">Total Volume</span>
            <span className="stat-subtitle">24h Trading</span>
          </div>
          <div className="stat-main-value">{formatNumber(globalStats.totalVolume)}</div>
          <div className="stat-breakdown">
            {globalStats.platforms && Object.entries(globalStats.platforms)
              .filter(([_, stats]) => stats.volume > 0)
              .sort((a, b) => b[1].volume - a[1].volume)
              .map(([platform, stats]) => (
                <div key={platform} className="stat-platform">
                  <span className="platform-name">{platform.charAt(0).toUpperCase() + platform.slice(1)}</span>
                  <span className="platform-value">{formatNumber(stats.volume)}</span>
                </div>
              ))
            }
          </div>
        </div>
        
        <div className="stat-group">
          <div className="stat-header">
            <span className="stat-title">Total Liquidity</span>
            <span className="stat-subtitle">Open Interest</span>
          </div>
          <div className="stat-main-value">{formatNumber(globalStats.totalLiquidity)}</div>
          <div className="stat-breakdown">
            {globalStats.platforms && Object.entries(globalStats.platforms)
              .filter(([_, stats]) => stats.liquidity > 0)
              .sort((a, b) => b[1].liquidity - a[1].liquidity)
              .map(([platform, stats]) => (
                <div key={platform} className="stat-platform">
                  <span className="platform-name">{platform.charAt(0).toUpperCase() + platform.slice(1)}</span>
                  <span className="platform-value">{formatNumber(stats.liquidity)}</span>
                </div>
              ))
            }
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="filter-controls">
          <input
            type="text"
            placeholder="Search markets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          
          {/* Filter Dropdown Buttons */}
          <div className="filter-dropdowns" ref={dropdownRef}>
            {/* Platform Filter */}
            <div className="filter-dropdown-container">
              <button
                className={`filter-dropdown-btn ${selectedPlatforms.length < 4 ? 'active' : ''}`}
                onClick={() => setActiveDropdown(activeDropdown === 'platforms' ? null : 'platforms')}
              >
                {selectedPlatforms.length === 0 ? 'No Markets' : 
                 selectedPlatforms.length === 4 ? 'All Markets' :
                 `${selectedPlatforms.length} Markets`}
                <span className="dropdown-arrow">▼</span>
              </button>
              {activeDropdown === 'platforms' && (
                <div className="filter-dropdown-panel">
                  <div className="dropdown-header">Select Markets</div>
                  <div className="dropdown-content">
                    <div className="platform-checkbox">
                      <label>
                        <input
                          type="checkbox"
                          checked={selectedPlatforms.length === 4}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedPlatforms(['polymarket', 'kalshi', 'predictit', 'limitless'])
                            } else {
                              setSelectedPlatforms([])
                            }
                            setMarketsPage(1)
                          }}
                        />
                        <span style={{ fontWeight: 600 }}>Select all</span>
                      </label>
                    </div>
                    <div style={{ borderTop: '1px solid var(--sacred-border)', marginTop: '0.5rem', paddingTop: '0.5rem' }}>
                      <div className="platform-checkbox">
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minHeight: '20px' }}>
                          <input
                            type="checkbox"
                            checked={selectedPlatforms.includes('polymarket')}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedPlatforms([...selectedPlatforms, 'polymarket'])
                              } else {
                                setSelectedPlatforms(selectedPlatforms.filter(p => p !== 'polymarket'))
                              }
                              setMarketsPage(1)
                            }}
                          />
                          <div style={{ display: 'flex', alignItems: 'center', width: '100px', paddingLeft: '4px' }}>
                            <PlatformLogo platform="polymarket" size={16} showText={false} />
                          </div>
                          <span style={{ marginLeft: 'auto', fontSize: '0.7rem', color: 'var(--sacred-text-dim)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            ({globalStats.platforms?.polymarket?.count || 0})
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                e.preventDefault()
                                setSelectedPlatforms(['polymarket'])
                                setMarketsPage(1)
                              }}
                              style={{
                                fontSize: '0.6rem',
                                padding: '0.125rem 0.375rem',
                                backgroundColor: 'transparent',
                                border: '1px solid var(--sacred-border)',
                                borderRadius: '2px',
                                color: 'var(--sacred-text-dim)',
                                cursor: 'pointer',
                                transition: 'all 0.15s'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = 'var(--sacred-accent)'
                                e.currentTarget.style.color = 'var(--sacred-accent)'
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = 'var(--sacred-border)'
                                e.currentTarget.style.color = 'var(--sacred-text-dim)'
                              }}
                            >
                              Select
                            </button>
                          </span>
                        </label>
                      </div>
                      <div className="platform-checkbox">
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minHeight: '20px' }}>
                          <input
                            type="checkbox"
                            checked={selectedPlatforms.includes('kalshi')}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedPlatforms([...selectedPlatforms, 'kalshi'])
                              } else {
                                setSelectedPlatforms(selectedPlatforms.filter(p => p !== 'kalshi'))
                              }
                              setMarketsPage(1)
                            }}
                          />
                          <div style={{ display: 'flex', alignItems: 'center', width: '100px', paddingLeft: '4px' }}>
                            <PlatformLogo platform="kalshi" size={16} showText={false} />
                          </div>
                          <span style={{ marginLeft: 'auto', fontSize: '0.7rem', color: 'var(--sacred-text-dim)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            ({globalStats.platforms?.kalshi?.count || 0})
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                e.preventDefault()
                                setSelectedPlatforms(['kalshi'])
                                setMarketsPage(1)
                              }}
                              style={{
                                fontSize: '0.6rem',
                                padding: '0.125rem 0.375rem',
                                backgroundColor: 'transparent',
                                border: '1px solid var(--sacred-border)',
                                borderRadius: '2px',
                                color: 'var(--sacred-text-dim)',
                                cursor: 'pointer',
                                transition: 'all 0.15s'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = 'var(--sacred-accent)'
                                e.currentTarget.style.color = 'var(--sacred-accent)'
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = 'var(--sacred-border)'
                                e.currentTarget.style.color = 'var(--sacred-text-dim)'
                              }}
                            >
                              Select
                            </button>
                          </span>
                        </label>
                      </div>
                      <div className="platform-checkbox">
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minHeight: '20px' }}>
                          <input
                            type="checkbox"
                            checked={selectedPlatforms.includes('predictit')}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedPlatforms([...selectedPlatforms, 'predictit'])
                              } else {
                                setSelectedPlatforms(selectedPlatforms.filter(p => p !== 'predictit'))
                              }
                              setMarketsPage(1)
                            }}
                          />
                          <div style={{ display: 'flex', alignItems: 'center', width: '100px', paddingLeft: '4px' }}>
                            <PlatformLogo platform="predictit" size={16} showText={false} />
                          </div>
                          <span style={{ marginLeft: 'auto', fontSize: '0.7rem', color: 'var(--sacred-text-dim)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            ({globalStats.platforms?.predictit?.count || 0})
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                e.preventDefault()
                                setSelectedPlatforms(['predictit'])
                                setMarketsPage(1)
                              }}
                              style={{
                                fontSize: '0.6rem',
                                padding: '0.125rem 0.375rem',
                                backgroundColor: 'transparent',
                                border: '1px solid var(--sacred-border)',
                                borderRadius: '2px',
                                color: 'var(--sacred-text-dim)',
                                cursor: 'pointer',
                                transition: 'all 0.15s'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = 'var(--sacred-accent)'
                                e.currentTarget.style.color = 'var(--sacred-accent)'
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = 'var(--sacred-border)'
                                e.currentTarget.style.color = 'var(--sacred-text-dim)'
                              }}
                            >
                              Select
                            </button>
                          </span>
                        </label>
                      </div>
                      <div className="platform-checkbox">
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minHeight: '20px' }}>
                          <input
                            type="checkbox"
                            checked={selectedPlatforms.includes('limitless')}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedPlatforms([...selectedPlatforms, 'limitless'])
                              } else {
                                setSelectedPlatforms(selectedPlatforms.filter(p => p !== 'limitless'))
                              }
                              setMarketsPage(1)
                            }}
                          />
                          <div style={{ display: 'flex', alignItems: 'center', width: '100px', paddingLeft: '4px' }}>
                            <PlatformLogo platform="limitless" size={16} showText={false} />
                          </div>
                          <span style={{ marginLeft: 'auto', fontSize: '0.7rem', color: 'var(--sacred-text-dim)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            ({globalStats.platforms?.limitless?.count || 0})
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                e.preventDefault()
                                setSelectedPlatforms(['limitless'])
                                setMarketsPage(1)
                              }}
                              style={{
                                fontSize: '0.6rem',
                                padding: '0.125rem 0.375rem',
                                backgroundColor: 'transparent',
                                border: '1px solid var(--sacred-border)',
                                borderRadius: '2px',
                                color: 'var(--sacred-text-dim)',
                                cursor: 'pointer',
                                transition: 'all 0.15s'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = 'var(--sacred-accent)'
                                e.currentTarget.style.color = 'var(--sacred-accent)'
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = 'var(--sacred-border)'
                                e.currentTarget.style.color = 'var(--sacred-text-dim)'
                              }}
                            >
                              Select
                            </button>
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Type Filter */}
            <div className="filter-dropdown-container">
              <button
                className={`filter-dropdown-btn ${selectedMarketTypes.length === 1 ? 'active' : ''}`}
                onClick={() => setActiveDropdown(activeDropdown === 'types' ? null : 'types')}
              >
                {getFilterLabel('types')}
                <span className="dropdown-arrow">▼</span>
              </button>
              {activeDropdown === 'types' && (
                <div className="filter-dropdown-panel">
                  <div className="dropdown-header">Market Types</div>
                  <div className="dropdown-content">
                    <div className="platform-checkbox">
                      <label>
                        <input
                          type="checkbox"
                          checked={selectedMarketTypes.length === 2}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedMarketTypes(['binary', 'group'])
                            } else {
                              setSelectedMarketTypes([])
                            }
                            setMarketsPage(1)
                          }}
                        />
                        <span style={{ fontWeight: 600 }}>Select all</span>
                      </label>
                    </div>
                    <div style={{ borderTop: '1px solid var(--sacred-border)', marginTop: '0.5rem', paddingTop: '0.5rem' }}>
                      <div className="platform-checkbox">
                        <label>
                          <input
                            type="checkbox"
                            checked={selectedMarketTypes.includes('binary')}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedMarketTypes([...selectedMarketTypes, 'binary'])
                              } else {
                                setSelectedMarketTypes(selectedMarketTypes.filter(t => t !== 'binary'))
                              }
                              setMarketsPage(1)
                            }}
                          />
                          <span>BINARY</span>
                        </label>
                      </div>
                      <div className="platform-checkbox">
                        <label>
                          <input
                            type="checkbox"
                            checked={selectedMarketTypes.includes('group')}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedMarketTypes([...selectedMarketTypes, 'group'])
                              } else {
                                setSelectedMarketTypes(selectedMarketTypes.filter(t => t !== 'group'))
                              }
                              setMarketsPage(1)
                            }}
                          />
                          <span>GROUP</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Volume Filter */}
            <div className="filter-dropdown-container">
              <button
                className={`filter-dropdown-btn ${volumeFilter.min > 0 || volumeFilter.max !== null ? 'active' : ''}`}
                onClick={() => setActiveDropdown(activeDropdown === 'volume' ? null : 'volume')}
              >
                {getFilterLabel('volume')}
                <span className="dropdown-arrow">▼</span>
              </button>
              {activeDropdown === 'volume' && (
                <div className="filter-dropdown-panel">
                  <div className="dropdown-header">Volume Filter</div>
                  <div className="dropdown-content">
                    <div className="range-row">
                      <label>Min</label>
                      <input
                        type="number"
                        placeholder="$0"
                        value={volumeFilter.min || ''}
                        onChange={(e) => setVolumeFilter(prev => ({ ...prev, min: Number(e.target.value) || 0 }))}
                        className="dropdown-input"
                      />
                    </div>
                    <div className="range-row">
                      <label>Max</label>
                      <input
                        type="number"
                        placeholder="No max"
                        value={volumeFilter.max || ''}
                        onChange={(e) => setVolumeFilter(prev => ({ ...prev, max: e.target.value ? Number(e.target.value) : null }))}
                        className="dropdown-input"
                      />
                    </div>
                    <div className="dropdown-actions">
                      <button className="dropdown-clear-btn" onClick={() => setVolumeFilter({ min: 0, max: null })}>
                        Clear
                      </button>
                      <button className="dropdown-apply-btn" onClick={() => {
                        setActiveDropdown(null)
                        setMarketsPage(1)
                      }}>
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Liquidity Filter */}
            <div className="filter-dropdown-container">
              <button
                className={`filter-dropdown-btn ${liquidityFilter.min > 0 || liquidityFilter.max !== null ? 'active' : ''}`}
                onClick={() => setActiveDropdown(activeDropdown === 'liquidity' ? null : 'liquidity')}
              >
                {getFilterLabel('liquidity')}
                <span className="dropdown-arrow">▼</span>
              </button>
              {activeDropdown === 'liquidity' && (
                <div className="filter-dropdown-panel">
                  <div className="dropdown-header">Liquidity Filter</div>
                  <div className="dropdown-content">
                    <div className="range-row">
                      <label>Min</label>
                      <input
                        type="number"
                        placeholder="$0"
                        value={liquidityFilter.min || ''}
                        onChange={(e) => setLiquidityFilter(prev => ({ ...prev, min: Number(e.target.value) || 0 }))}
                        className="dropdown-input"
                      />
                    </div>
                    <div className="range-row">
                      <label>Max</label>
                      <input
                        type="number"
                        placeholder="No max"
                        value={liquidityFilter.max || ''}
                        onChange={(e) => setLiquidityFilter(prev => ({ ...prev, max: e.target.value ? Number(e.target.value) : null }))}
                        className="dropdown-input"
                      />
                    </div>
                    <div className="dropdown-actions">
                      <button className="dropdown-clear-btn" onClick={() => setLiquidityFilter({ min: 0, max: null })}>
                        Clear
                      </button>
                      <button className="dropdown-apply-btn" onClick={() => {
                        setActiveDropdown(null)
                        setMarketsPage(1)
                      }}>
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Spread Filter */}
            <div className="filter-dropdown-container">
              <button
                className={`filter-dropdown-btn ${spreadFilter.min > 0 || spreadFilter.max < 100 ? 'active' : ''}`}
                onClick={() => setActiveDropdown(activeDropdown === 'spread' ? null : 'spread')}
              >
                {getFilterLabel('spread')}
                <span className="dropdown-arrow">▼</span>
              </button>
              {activeDropdown === 'spread' && (
                <div className="filter-dropdown-panel">
                  <div className="dropdown-header">Spread Filter</div>
                  <div className="dropdown-content">
                    <div className="range-row">
                      <label>Min %</label>
                      <input
                        type="number"
                        placeholder="0%"
                        value={spreadFilter.min || ''}
                        onChange={(e) => setSpreadFilter(prev => ({ ...prev, min: Number(e.target.value) || 0 }))}
                        className="dropdown-input"
                        min="0"
                        max="100"
                        step="0.1"
                      />
                    </div>
                    <div className="range-row">
                      <label>Max %</label>
                      <input
                        type="number"
                        placeholder="100%"
                        value={spreadFilter.max || ''}
                        onChange={(e) => setSpreadFilter(prev => ({ ...prev, max: Number(e.target.value) || 100 }))}
                        className="dropdown-input"
                        min="0"
                        max="100"
                        step="0.1"
                      />
                    </div>
                    <div className="dropdown-actions">
                      <button className="dropdown-clear-btn" onClick={() => setSpreadFilter({ min: 0, max: 100 })}>
                        Clear
                      </button>
                      <button className="dropdown-apply-btn" onClick={() => {
                        setActiveDropdown(null)
                        setMarketsPage(1)
                      }}>
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Show Ended Markets Toggle */}
            <label className="show-ended-toggle" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginLeft: 'auto',
              padding: '0.375rem 0.75rem',
              borderRadius: '4px',
              backgroundColor: showEndedMarkets ? 'rgba(255, 59, 48, 0.1)' : 'transparent',
              border: '1px solid var(--sacred-border)',
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontSize: '0.8rem',
              color: showEndedMarkets ? '#ff3b30' : 'var(--sacred-text-dim)',
              fontWeight: showEndedMarkets ? 500 : 400,
              userSelect: 'none'
            }}>
              <input
                type="checkbox"
                checked={showEndedMarkets}
                onChange={(e) => {
                  setShowEndedMarkets(e.target.checked)
                  setMarketsPage(1)
                }}
                style={{ 
                  cursor: 'pointer',
                  margin: 0,
                  width: '14px',
                  height: '14px'
                }}
              />
              Show Ended Markets
            </label>
            
            {/* Clear Filters Button */}
            {hasActiveFilters() && (
              <button
                className="filter-dropdown-btn active"
                onClick={resetAllFilters}
                style={{
                  marginLeft: '0.5rem',
                  backgroundColor: 'rgba(255, 0, 0, 0.05)',
                  borderColor: '#ff3b30',
                  color: '#ff3b30'
                }}
              >
                Clear Filters
                <span style={{ fontSize: '0.5rem' }}>✕</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="markets-table-container">
        {loading && marketsPage === 1 ? (
          <div className="loading">Loading markets...</div>
        ) : error ? (
          <div className="error">{error}</div>
        ) : table.getRowModel().rows.length === 0 ? (
          <div className="empty-state">
            <h3>No markets found</h3>
            <p>Try adjusting your filters or search query</p>
          </div>
        ) : (
          <>
          <table className="markets-table">
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th 
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      className={header.column.getCanSort() ? 'sortable' : ''}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      {header.column.getIsSorted() && (
                        <span className="sort-indicator">
                          {header.column.getIsSorted() === 'desc' ? ' ↓' : ' ↑'}
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row, index) => (
                <Fragment key={row.id}>
                  <tr 
                    data-market-index={index}
                    className={row.getIsExpanded() ? 'expanded' : ''}
                    onClick={async (e) => {
                      e.stopPropagation()
                      const market = row.original
                      
                      console.log('Row clicked:', { market })
                      
                      // Open drawer for all markets, including groups
                      handleMarketClick(market)
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                </Fragment>
              ))}
            </tbody>
          </table>
          </>
        )}
        
        {/* Pagination Footer */}
        {totalMarkets > 0 && markets.length > 0 && (
          <div style={{ 
            borderTop: '1px solid var(--sacred-border)',
            backgroundColor: 'var(--sacred-bg-secondary)',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0.75rem 1rem',
              fontSize: '0.8rem'
            }}>
              <div style={{ color: 'var(--sacred-text-dim)' }}>
                Showing <span style={{ color: 'var(--sacred-text)', fontWeight: 600 }}>{markets.length.toLocaleString()}</span> of <span style={{ color: 'var(--sacred-text)', fontWeight: 600 }}>{totalMarkets.toLocaleString()}</span> markets
                {(volumeFilter.min > 0 || volumeFilter.max !== null || 
                  liquidityFilter.min > 0 || liquidityFilter.max !== null || 
                  spreadFilter.min > 0 || spreadFilter.max < 100 || 
                  debouncedSearchQuery) && 
                  <span style={{ 
                    color: 'var(--sacred-accent)',
                    marginLeft: '0.5rem',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>• filtered</span>
                }
              </div>
              
              {hasMoreMarkets && (
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <button 
                    onClick={() => setMarketsPage(prev => prev + 1)}
                    disabled={loading || loadingAll}
                    style={{
                      padding: '0.375rem 0.75rem',
                      backgroundColor: 'var(--sacred-bg)',
                      border: '1px solid var(--sacred-border)',
                      borderRadius: '3px',
                      color: 'var(--sacred-text)',
                      cursor: loading || loadingAll ? 'not-allowed' : 'pointer',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      transition: 'all 0.15s',
                      opacity: loading || loadingAll ? 0.5 : 1,
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => {
                      if (!loading && !loadingAll) {
                        e.currentTarget.style.backgroundColor = 'var(--sacred-bg-hover)'
                        e.currentTarget.style.borderColor = 'var(--sacred-accent)'
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!loading && !loadingAll) {
                        e.currentTarget.style.backgroundColor = 'var(--sacred-bg)'
                        e.currentTarget.style.borderColor = 'var(--sacred-border)'
                      }
                    }}
                  >
                    {loading ? 'Loading...' : `Load ${Math.min(totalMarkets - markets.length, 1000).toLocaleString()} More`}
                  </button>
                  
                  {totalMarkets - markets.length > 1000 && (
                    <>
                      <span style={{ color: 'var(--sacred-text-dim)', fontSize: '0.7rem' }}>or</span>
                      <button 
                        onClick={() => {
                          if (!loadingAll) {
                            setLoadingAll(true)
                            const remainingPages = Math.ceil((totalMarkets - markets.length) / 1000)
                            let loadedPages = 0
                            for (let i = 0; i < remainingPages; i++) {
                              setTimeout(() => {
                                setMarketsPage(prev => prev + 1)
                                loadedPages++
                                if (loadedPages === remainingPages) {
                                  setLoadingAll(false)
                                }
                              }, i * 100)
                            }
                          }
                        }}
                        disabled={loadingAll || loading}
                        style={{
                          padding: '0.375rem 0.75rem',
                          backgroundColor: 'transparent',
                          border: '1px solid var(--sacred-text-dim)',
                          borderRadius: '3px',
                          color: 'var(--sacred-text-dim)',
                          cursor: loadingAll || loading ? 'not-allowed' : 'pointer',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          transition: 'all 0.15s',
                          opacity: loadingAll || loading ? 0.5 : 1,
                          whiteSpace: 'nowrap'
                        }}
                        onMouseEnter={(e) => {
                          if (!loadingAll && !loading) {
                            e.currentTarget.style.borderColor = 'var(--sacred-accent)'
                            e.currentTarget.style.color = 'var(--sacred-accent)'
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!loadingAll && !loading) {
                            e.currentTarget.style.borderColor = 'var(--sacred-text-dim)'
                            e.currentTarget.style.color = 'var(--sacred-text-dim)'
                          }
                        }}
                      >
                        {loadingAll ? 'Loading All...' : `Load All ${(totalMarkets - markets.length).toLocaleString()}`}
                      </button>
                    </>
                  )}
                </div>
              )}
              
              {!hasMoreMarkets && markets.length === totalMarkets && (
                <div style={{ 
                  color: 'var(--sacred-success)', 
                  fontSize: '0.75rem',
                  fontWeight: 500
                }}>
                  All markets loaded
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Market Drawer */}
      {selectedMarket && (
        <MarketDrawer
          isOpen={isDrawerOpen}
          onClose={() => {
            setIsDrawerOpen(false)
            setSelectedMarket(null)
            
            // Remove market parameter from URL
            const params = new URLSearchParams(window.location.search)
            params.delete('m')
            const newUrl = params.toString() 
              ? `${window.location.pathname}?${params.toString()}`
              : window.location.pathname
            window.history.pushState({}, '', newUrl)
          }}
          market={selectedMarket}
          enhancedData={enhancedMarketData[`${selectedMarket.platform}_${selectedMarket.id}`]}
        />
      )}
    </div>
  )
}